import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs/operators';
import { MessageBoxCustomButton, MessageDialogComponent } from 'src/app/angular-core/components/message-dialog/message-dialog.component';
import { DeviceSubscriptionOfferExt } from 'src/app/generated/CrossPoint.License.Server';
import { IPermission } from 'src/app/shared/guards/authorized.guard';
import { createLicensesOrderAction, LicensesActionsTypes, validateLicensesAction } from 'src/app/store/actions/licenses.actions';
import { selectShoppingBasket } from 'src/app/store/selectors/licenses.selectors';
import { OperationEnum } from 'src/app/utils/enums/operation.enums';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';
import { LicenseFormatterService } from '../../services/license-formatter.service';
import { ShoppingBasketService } from '../../services/shopping-basket.service';
import { getCurrencySymbol } from 'src/app/utils/functions/currency.functions';
import { MatDialogRef, MatDialog } from '@angular/material/dialog';

@UntilDestroy()
@Component({
  selector: 'xpac-order-licenses',
  templateUrl: './order-licenses.component.html',
  styleUrls: ['./order-licenses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class OrderLicensesComponent implements OnInit {
  private static readonly DEFAULT_CURRENCY: string = '€';

  private static readonly ORDER_COPY_EMAIL_FORM = {
    orderCopyEmail: [{ value: '', disabled: false }, { validators: [Validators.email] }]
  }
  
  // For more info about number formatting, see https://angular.io/api/common/DecimalPipe
  // Formatted as: {minIntegerDigits}.{minFractionDigits}-{maxFractionDigits}
  public readonly PRICE_FORMAT: string = '1.2-2';

  public selectedLicenses: DeviceSubscriptionOfferExt[] = [];
  public selectedNewLicenses: DeviceSubscriptionOfferExt[] = [];
  public selectedCancelledLicenses: DeviceSubscriptionOfferExt[] = [];
  public displayedColumnsNewLicenses = ['store', 'total', 'remove'];
  public displayedColumnsCancelledLicenses = ['store', 'remove'];

  public orderFormGroup: FormGroup;

  public isSendingOrder: boolean = false;
  public isOrdered: boolean = false;
  public orderedSuccessfully: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<OrderLicensesComponent>,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private licenseFormatterService: LicenseFormatterService,
    private shoppingBasketService: ShoppingBasketService,
    private store: Store<IRemoteXPACPortalState>,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
    private actionsListener$: Actions,
  ) { }

  public ngOnInit(): void {
    this.isSendingOrder = false;
    this.isOrdered = false;
    
    this.initForm();
    this.initialize();    
  }

  public agGetRequiredPermissions(): IPermission[] {
    return [
      { type: 'RemoteMaintenance.License', operation: OperationEnum.READ }
    ];
  }

  private initialize(): void {
    this.store.select(selectShoppingBasket)
      .pipe(
        filter<DeviceSubscriptionOfferExt[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((selectedLicenses: DeviceSubscriptionOfferExt[]) => {
        this.selectedLicenses = selectedLicenses;
        //always validate 
        this.store.dispatch(validateLicensesAction({ data: { licenses: this.selectedLicenses } }));
        this.filterLicenses();
        this.cdr.markForCheck();
      });

    // Listen to order sucess
    this.actionsListener$
      .pipe(ofType(LicensesActionsTypes.CREATE_LICENSE_ORDER_SUCCESS))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.isSendingOrder) {
          this.shoppingBasketService.clear();
          this.isSendingOrder = false;
          this.isOrdered = false;
          this.orderedSuccessfully = true;
        }

        this.cdr.markForCheck();
      });

    // listen to order failed
    this.actionsListener$
      .pipe(ofType(LicensesActionsTypes.CREATE_LICENSE_ORDER_FAILED))
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        if (this.isSendingOrder) {
          this.isSendingOrder = false;
          this.isOrdered = false;
        }

        this.cdr.markForCheck();
      });
  }

  public onNoClick(): void { 
    if(!this.isSendingOrder){
      this.dialogRef.close(true);
    }    
  }

  public removeLicense(license: DeviceSubscriptionOfferExt): void{
    this.shoppingBasketService.remove(license);
  }

  public getTotalLicensePrice(license: DeviceSubscriptionOfferExt): number {
    return license.basePrice + license.reconnectionFee;
  }
  
  public getTotalOrderPrice(): number {
    const totalOrderPrice: number = this.selectedNewLicenses.reduce((sum: number, current: DeviceSubscriptionOfferExt) => sum + (this.getTotalLicensePrice(current)), 0);

    return totalOrderPrice;
  }

  public getStoreTitle(license: DeviceSubscriptionOfferExt): string {
    return this.licenseFormatterService.getFormattedLicenseTitle(license);
  }

  public getStoreSubTitle(license: DeviceSubscriptionOfferExt): string {
    return this.licenseFormatterService.getFormattedLicenseSubTitle(license);
  }

  public getCurrency(): string {
    const currency: string = this.selectedLicenses[0]?.currency;
    if (currency) {
      return getCurrencySymbol(currency);
    }
    return OrderLicensesComponent.DEFAULT_CURRENCY;   
  }

  public onSendOrderClick(): void {
    const copyEmail: string = this.orderFormGroup.value.orderCopyEmail;

    if (!this.orderFormGroup.valid) {
      const header: string = this.translate.instant("Modules.Licenses.Orders.InvalidEmailAddress");
      const message: string = this.translate.instant("Modules.Licenses.Orders.PleaseEnterAValidEmailAddress");

      this.showDialog(header, message);
      return;
    }

    this.sendOrder(copyEmail);
  }

  private initForm(): void {
    this.orderFormGroup = this.formBuilder.group(OrderLicensesComponent.ORDER_COPY_EMAIL_FORM);
  }

  private filterLicenses(): void{

    this.selectedCancelledLicenses = [];
    this.selectedNewLicenses = [];

    this.selectedLicenses.forEach((license: DeviceSubscriptionOfferExt) => {
      if(license.months === 0){
        this.selectedCancelledLicenses.push(license);
      }
      else{
        this.selectedNewLicenses.push(license);
      }
    });
  }

  private sendOrder(copyEmail: string): void {
    this.isSendingOrder = true;
    this.isOrdered = true;
    this.cdr.markForCheck();

    this.store.dispatch(createLicensesOrderAction({ 
      data: {
        copyEmail: copyEmail,
        deviceSubscriptionOffer: this.selectedLicenses
      }
    }));
  }

  private showDialog(header: string, message: string): void {
    this.dialog.open(MessageDialogComponent, {
      panelClass: 'messagebox-dialog',
      autoFocus: false,
      data: {
        headerInfo: header, 
        message: message, 
        buttons: MessageBoxCustomButton.OK
      }
    });
  }
}
